import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, pipe, tap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { tapResponse } from '@ngrx/operators';
import { Router } from '@angular/router';
import { HttpRequest } from '@angular/common/http';

export interface AuthState {
  token: string;
  error?: string;
  isLoggedIn: boolean;
  hasToken: boolean;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  token: '',
  error: '',
  isLoggedIn: false,
  hasToken: false,
  isAuthenticated: false,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    _authService: inject(AuthService),
    _router: inject(Router),
  })),
  withMethods((store) => {
    const login = rxMethod<{ email: string; password: string }>(
      pipe(
        tap(() => patchState(store)),
        exhaustMap((credentials) =>
          store._authService.login(credentials).pipe(
            tapResponse({
              next: (response) => {
                const token = response.token.jwtToken;
                const expiresAt = Date.now() + 6 * 60 * 60 * 1000; // 6 hours
                localStorage.setItem('access_token', token);
                localStorage.setItem('token_expires_at', expiresAt.toString());

                patchState(store, {
                  token,
                  isLoggedIn: true,
                  hasToken: true,
                  isAuthenticated: true,
                });
                store._router.navigate(['/home']);
              },
              error: (error: Error) => {
                patchState(store, {
                  token: '',
                  error: error.message,
                  hasToken: false,
                  isAuthenticated: false,
                });
              },
            }),
          ),
        ),
      ),
    );

    const logout = () => {
      localStorage.removeItem('access_token');
      patchState(store, {
        token: '',
        error: '',
        hasToken: false,
        isAuthenticated: false,
      });
      store._router.navigate(['/login']);
    };

    function initAuth(): void {
      const token = localStorage.getItem('access_token');
      const expiry = parseInt(
        localStorage.getItem('token_expires_at') || '0',
        10,
      );

      if (token && Date.now() < expiry) {
        patchState(store, {
          token,
          isLoggedIn: true,
          hasToken: true,
          isAuthenticated: true,
        });
      } else {
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_expires_at');
      }
    }

    function checkAuthenticationAndRedirect(): boolean {
      const token = localStorage.getItem('access_token');
      const expiry = parseInt(
        localStorage.getItem('token_expires_at') || '0',
        10,
      );

      const isValid = !!token && Date.now() < expiry;

      patchState(store, {
        isAuthenticated: isValid,
        hasToken: !!token,
        isLoggedIn: isValid,
      });

      if (!isValid) {
        store._router.navigate(['/login']);
      }

      return isValid;
    }

    function getAuthRequest<T>(req: HttpRequest<T>): HttpRequest<T> {
      const token = store.token();

      return token
        ? req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          })
        : req;
    }

    return {
      login,
      logout,
      initAuth,
      checkAuthenticationAndRedirect,
      getAuthRequest,
    };
  }),

  withHooks({
    onInit(store) {
      store.initAuth();
    },
  }),
);
